"use client";

import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import { cn } from "@workspace/ui/lib";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { AnimateChangeInHeight } from "~/components/AnimateChangeInHeight";
import LoadingSpinner from "~/components/LoadingSpinner";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { MagicLinkSignInButton } from "./MagicLinkSignInButton";

type FormData = {
  email: string;
};

export const WaitlistAndLogin = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  return (
    <AnimateChangeInHeight>
      {submitStatus === "idle" || submitStatus === "error" ? (
        <>
          <p className="mb-7 text-sm">
            We&apos;re onboarding new users each week - sign up to the next
            batch below.
          </p>
          <Form
            isSubmitting={isSubmitting}
            submitStatus={submitStatus}
            setIsSubmitting={setIsSubmitting}
            setSubmitStatus={setSubmitStatus}
          />
          <Login />
        </>
      ) : (
        <ThanksMessage
          reset={() => {
            setIsSubmitting(false);
            setSubmitStatus("idle");
          }}
        />
      )}
    </AnimateChangeInHeight>
  );
};

// Sub-components

const Form = ({
  isSubmitting,
  submitStatus,
  setIsSubmitting,
  setSubmitStatus,
}: {
  isSubmitting: boolean;
  submitStatus: "idle" | "success" | "error";
  setIsSubmitting: (isSubmitting: boolean) => void;
  setSubmitStatus: (submitStatus: "idle" | "success" | "error") => void;
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true);
      setSubmitStatus("idle");

      await fetch("/api/waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: data.email }),
      });

      setSubmitStatus("success");
      reset();
    } catch {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(onSubmit)(e);
      }}
      className="p-[1px]"
    >
      <h3 className="mb-1.5 text-xs font-semibold">Email</h3>
      <Input
        {...register("email", {
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address",
          },
        })}
        placeholder="Your email address"
        className="mb-1 text-xs"
      />
      {errors.email && (
        <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
      )}
      {submitStatus === "error" && (
        <p className="mt-1 rounded-md bg-red-200 px-1.5 py-1 text-xs text-red-500">
          Something went wrong. Please try again.
        </p>
      )}
      <Button
        type="submit"
        disabled={isSubmitting}
        className={cn(
          "mt-2 flex w-full justify-between py-2 pl-3 pr-2 text-xs transition-opacity",
          isSubmitting && "opacity-80"
        )}
      >
        <span>Sign up to waitlist</span>
        {isSubmitting ? (
          <LoadingSpinner size={14} className="flex-shrink-0" color="white" />
        ) : (
          <ArrowRight size={14} className="flex-shrink-0 font-bold" />
        )}
      </Button>
    </form>
  );
};

const Login = () => {
  return (
    <>
      <div className="relative my-4 w-full text-center">
        <div className="absolute inset-0 flex items-center">
          <div className="relative top-0.5 w-full border-t border-gray-200" />
        </div>
        <div className="relative">
          <span className="bg-white px-2 text-xs font-semibold text-gray-300">
            OR
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <GoogleSignInButton />
        <MagicLinkSignInButton />
      </div>
    </>
  );
};

const ThanksMessage = ({ reset }: { reset: () => void }) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm">
        Thanks for joining the waitlist! We&apos;ll let you know when the next
        iteration of CodeWords is launched.
      </p>
      <div className="mt-2">
        <Button
          variant="ghost"
          className="flex gap-1 bg-transparent p-0 text-xs"
          onClick={reset}
        >
          <ArrowLeft size={14} className="flex-shrink-0 font-bold" />
          <span>Back</span>
        </Button>
      </div>
    </div>
  );
};
